import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'publishBtn',
    'publishBtnTooltip',
    'resetBtn',
    'form',
    'check',
  ]

  static values = {
    checksAlreadyUsedCount: Number,
    id: Number,
    name: String,
  }

  private publishBtnTarget!: HTMLElement
  private hasPublishBtnTarget!: boolean
  private publishBtnTooltipTarget!: HTMLElement
  private hasPublishBtnTooltipTarget!: boolean
  private resetBtnTarget!: HTMLElement
  private hasResetBtnTarget!: boolean
  private formTarget!: HTMLFormElement
  private checkTargets!: NodeListOf<HTMLElement>

  private submitForm!: () => void
  private resetForm!: () => void

  private checksAlreadyUsedCountValue!: number
  private idValue!: number
  private nameValue!: string
  private isDirty: boolean = false
  private mutationObserver: MutationObserver | null = null

  connect(): void {
    this.submitForm = this._submitForm.bind(this)
    this.resetForm = this._resetForm.bind(this)

    window.eventHub.$on('publish-check-list-confirmed', this.submitForm)
    window.eventHub.$on('reset-check-list-confirmed', this.resetForm)
    window.eventHub.$on('destroy-check-link', this.handleFormChange)

    document.addEventListener('input', this.handleDocumentFormInput, true)
    document.addEventListener('change', this.handleDocumentFormInput, true)

    this.associateFormInputs()
    this.setupChecksObserver()
  }

  disconnect(): void {
    window.eventHub.$off('publish-check-list-confirmed', this.submitForm)
    window.eventHub.$off('reset-check-list-confirmed', this.resetForm)
    window.eventHub.$off('destroy-check-link', this.handleFormChange)

    document.removeEventListener('input', this.handleDocumentFormInput, true)
    document.removeEventListener('change', this.handleDocumentFormInput, true)

    this.mutationObserver?.disconnect()
    this.mutationObserver = null
  }

  private handlePublishBtnClick = (event: Event): void => {
    event.preventDefault()

    if (this.checksAlreadyUsedCountValue > 0) {
      const checks = document.querySelectorAll<HTMLElement>(
        '[data-check-list--edit-target="check"]'
      )
      const newChecks = [...checks].filter((el: HTMLElement) => {
        const description = el.querySelector(
          '.check_list_checks_description textarea'
        ) as HTMLTextAreaElement

        return !el.dataset.id && !!description?.value
      })

      window.modalCitizen.openModalPortal(
        'publish-check-list-confirmation-modal',
        { newChecksCount: newChecks.length }
      )
    } else {
      this.submitForm()
    }
  }

  private handleResetBtnClick = (event: Event): void => {
    event.preventDefault()
    window.modalCitizen.openModalPortal('reset-check-list-confirmation-modal')
  }

  private handleCopyBtnClick = (event: Event): void => {
    event.preventDefault()
    window.modalCitizen.openModalPortal('copy-check-list-modal', {
      id: this.idValue,
      name: this.nameValue,
    })
  }

  private handleFormChange = (): void => {
    this.isDirty = true
    this.updateDirtyState()
  }

  private handleDocumentFormInput = (event: Event): void => {
    const target = event.target as HTMLInputElement
    if (target.form === this.formTarget) {
      this.handleFormChange()
    }
  }

  private setupChecksObserver(): void {
    const checksContainer = document.querySelector(
      "[data-behaviour='check-list']"
    )
    if (!checksContainer || this.formTarget.contains(checksContainer)) return

    // Cocoon 1.x fires jQuery-only events, so we use MutationObserver to detect
    // check additions and removals when the form lives outside the content area.
    this.mutationObserver = new MutationObserver(() => {
      this.associateFormInputs()
      this.handleFormChange()
    })
    this.mutationObserver.observe(checksContainer, { childList: true })
  }

  private associateFormInputs(): void {
    document
      .querySelectorAll<HTMLInputElement>('[name^="check_list"]')
      .forEach((input) => {
        if (!this.formTarget.contains(input)) {
          input.setAttribute('form', this.formTarget.id)
        }
      })
  }

  private _resetForm(): void {
    this.isDirty = false
    this.updateDirtyState()
  }

  private findTarget(name: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(
      `[data-check-list--edit-target="${name}"]`
    )
  }

  private updateDirtyState(): void {
    const publishBtn = this.hasPublishBtnTarget
      ? this.publishBtnTarget
      : this.findTarget('publishBtn')
    const resetBtn = this.hasResetBtnTarget
      ? this.resetBtnTarget
      : this.findTarget('resetBtn')
    const publishBtnTooltip = this.hasPublishBtnTooltipTarget
      ? this.publishBtnTooltipTarget
      : this.findTarget('publishBtnTooltip')

    if (this.isDirty) {
      publishBtn?.classList.remove('sf-disabled')
      resetBtn?.classList.remove('tw-hidden')
      publishBtnTooltip?.classList.add('tw-hidden')
    } else {
      publishBtn?.classList.add('sf-disabled')
      resetBtn?.classList.add('tw-hidden')
      publishBtnTooltip?.classList.remove('tw-hidden')

      this.formTarget.reset()

      const formId = this.formTarget.id
      const destroyedInputs = document.querySelectorAll<HTMLInputElement>(
        `#${formId} [name*="[_destroy]"][value="1"], [form="${formId}"][name*="[_destroy]"][value="1"]`
      )
      destroyedInputs.forEach((el) => {
        el.value = ''

        const parent = el.closest(
          "[data-behaviour='check-fields']"
        ) as HTMLElement

        if (parent) {
          parent.style.removeProperty('display')
          parent.classList.add('tw-flex')
        }
      })
    }
  }

  private _submitForm(): void {
    this.formTarget.submit()
  }
}
